// assets/product-form.js
// Cache le bouton "Ajouter au panier" quand la variante sélectionnée est épuisée,
// et l’affiche lorsqu’une variante disponible est choisie.
// S’appuie sur un attribut data-available porté par <product-form> et un JSON d’availability
// optionnel rendu côté Liquid :
// <script type="application/json" id="VariantAvailability-{{ section.id }}">
// { "1234567890": true, "1234567891": false, ... }
// </script>

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        // Éléments principaux
        this.form = this.querySelector('form');
        if (!this.form) return;

        // Boutons (plusieurs contextes possibles : sticky, drawer…)
        this.submitButtons = Array.from(this.querySelectorAll('[type="submit"]'));
        this.submitButton  = this.submitButtons[0] || null;
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;
        this.soldOutMessage   = this.submitButton ? this.submitButton.querySelector('.sold-out-message') : null;

        // Bouton "notify me" Klaviyo (optionnel)
        this.notifyButton = this.querySelector('.klaviyo-bis-trigger');

        // Accessoires panier (cart-notification ou cart-drawer)
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (this.cart && document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        // Gestion d’erreurs
        this.hideErrors = this.dataset.hideErrors === 'true';

        // Champ de variante
        this.variantIdInput = this.form.querySelector('[name=id]');
        if (this.variantIdInput) this.variantIdInput.disabled = false;

        // Écouteurs
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        // Identification de section pour lire le JSON d’availability (si présent)
        this.sectionId = this.dataset.section
          || (this.closest('[id^="shopify-section-"]')?.id || '').replace('shopify-section-', '')
          || null;

        // Chargement de la map d’availability si rendue par Liquid
        this.availabilityMap = {};
        if (this.sectionId) {
          const avEl = document.getElementById(`VariantAvailability-${this.sectionId}`);
          if (avEl) {
            try { this.availabilityMap = JSON.parse(avEl.textContent || '{}'); } catch (e) { /* noop */ }
          }
        }

        // — Écoute des changements de variante —
        // 1) Thèmes OS2 (Dawn & dérivés) émettent `variant:change` avec detail.variant.available
        this._boundVariantChange = (evt) => {
          // S’assurer que l’événement concerne ce formulaire (même section / bloc)
          if (evt && evt.target && !this.contains(evt.target) && !this.form.contains(evt.target)) return;
          const variant = evt?.detail?.variant || null;
          this.refreshAvailability(variant);
        };
        document.addEventListener('variant:change', this._boundVariantChange, true);

        // 2) Fallback : écoute directe sur le champ name="id"
        if (this.variantIdInput) {
          this.variantIdInput.addEventListener('change', () => this.refreshAvailability(null));
        }

        // État initial au chargement
        this.refreshAvailability(null);
      }

      disconnectedCallback() {
        document.removeEventListener('variant:change', this._boundVariantChange, true);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.submitButton) return;

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Ajouter automatiquement une property si un champ hidden [data-property-name] est présent
        this.form.querySelectorAll('input[type="hidden"][data-property-name]').forEach((input) => {
          if (input.value && input.dataset.propertyName) {
            formData.append(`properties[${input.dataset.propertyName}]`, input.value);
          }
        });

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              // Erreur d’ajout au panier
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              if (this.submitButton) {
                const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                if (soldOutMessage) {
                  this.submitButton.setAttribute('aria-disabled', 'true');
                  if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
                  soldOutMessage.classList.remove('hidden');
                }
              }

              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (this.submitButton) this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && this.submitButton) this.submitButton.removeAttribute('aria-disabled');
            const spinner2 = this.querySelector('.loading__spinner');
            if (spinner2) spinner2.classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        // Gère tous les boutons ATC potentiels du composant
        this.submitButtons.forEach((btn) => {
          if (!btn) return;

          if (disable) {
            btn.setAttribute('disabled', 'disabled');
            if (text) {
              const span = btn.querySelector('span');
              if (span) span.textContent = text;
            }
          } else {
            btn.removeAttribute('disabled');
            const span = btn.querySelector('span');
            if (span && window.variantStrings && window.variantStrings.addToCart) {
              span.textContent = window.variantStrings.addToCart;
            }
          }
        });
      }

      /**
       * Met à jour l’UI selon la disponibilité de la variante.
       * - Pilote un attribut data-available sur <product-form>
       * - Laisse le CSS décider d’afficher/cacher les boutons
       * @param {Object|null} variant - si présent, doit contenir `available` (Dawn & dérivés).
       */
      refreshAvailability(variant) {
        const idInput = this.variantIdInput;
        if (!idInput) return;

        const variantId = idInput.value;

        let available;
        this.dataset.available = available ? 'true' : 'false';

        // 1) Source idéale : event.detail.variant.available
        if (variant && typeof variant.available === 'boolean') {
          available = variant.available;
        } else {
          // 2) Map JSON rendue côté Liquid (fiable si présente)
          if (this.availabilityMap && Object.prototype.hasOwnProperty.call(this.availabilityMap, variantId)) {
            available = !!this.availabilityMap[variantId];
          } else if (idInput.tagName === 'SELECT') {
            // 3) Heuristique depuis <select name="id">
            const opt = idInput.selectedOptions && idInput.selectedOptions[0];
            if (opt) {
              if (typeof opt.dataset.available !== 'undefined') {
                available = (opt.dataset.available === 'true');
              } else {
                available = !opt.hasAttribute('disabled');
              }
            } else {
              available = true; // fallback neutre
            }
          } else {
            available = true; // fallback neutre pour input hidden
          }
        }

        // Pilote par attribut : product-form[data-available="true|false"]
        this.dataset.available = available ? 'true' : 'false';

        // Nettoyage / cohérence UI interne (libellés, notify)
        if (available) {
          // 1) Ré-activer tous les boutons et retirer display:none inline si présent
          this.submitButtons.forEach((btn) => {
            if (!btn) return;
            btn.removeAttribute('aria-disabled');
            btn.classList.remove('hidden');
            btn.style.removeProperty('display'); // retire un éventuel display:none inline
            // Si, malgré tout, le bouton reste masqué (CSS externe), on force un fallback visible :
            const cs = window.getComputedStyle(btn);
            if (cs.display === 'none') {
              // Heuristique : full-width → block, sinon inline-flex pour conserver l’alignement
              if (btn.classList.contains('button--full-width') || btn.classList.contains('btn--full')) {
                btn.style.display = 'block';
              } else {
                btn.style.display = 'inline-flex';
              }
            }
          });

          // 2) Rétablir le texte/état visuel
          this.toggleSubmitButton(false);
          if (this.soldOutMessage) this.soldOutMessage.classList.add('hidden');
          if (this.submitButtonText) this.submitButtonText.classList.remove('hidden');

          // 3) Masquer le bouton "notify me" si présent
          if (this.notifyButton) this.notifyButton.style.display = 'none';
        } else {
          // 1) Désactiver et cacher complètement (et pas seulement disabled)
          this.toggleSubmitButton(true);
          this.submitButtons.forEach((btn) => {
            if (!btn) return;
            btn.setAttribute('aria-disabled', 'true');
            btn.classList.add('hidden');
            btn.style.display = 'none';
          });

          // 2) Afficher le bouton "notify me" si présent
          if (this.notifyButton) this.notifyButton.style.display = 'flex';
        }
      }
    }
  );
}

/**
 * Attendu dans le thème (Shopify) :
 * - fetchConfig('javascript')
 * - routes.cart_add_url / window.routes.cart_url
 * - publish / PUB_SUB_EVENTS
 * - window.variantStrings.addToCart
 *
 * CSS recommandé (assets/theme.css ou équivalent) :
 * product-form[data-available="false"] [type="submit"] { display: none !important; }
 * product-form[data-available="true"]  .klaviyo-bis-trigger { display: none !important; }
 * product-form[data-available="false"] .klaviyo-bis-trigger { display: flex !important; align-items: center; }
 */
